import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, Play, Activity, TrendingDown, BarChart3, Info } from 'lucide-react';

// Tab configuration
enum BacktestStrategy {
  PAIRS_TRADING = 'pairs',
  ALPHA_PORTFOLIO = 'alpha',
  BASKET_SPREAD = 'basket'
}

interface BacktestParams {
  // Pairs Trading
  asset1?: string;
  asset2?: string;
  days?: number;
  window?: number;
  entryZ?: number;
  exitZ?: number;
  
  // Alpha Portfolio
  universe?: string;
  startDate?: string;
  endDate?: string;
  portfolioSize?: number;
  lookbackDays?: number;
  alphaMetric?: string;
  buyDipPct?: number;
  sellDecayPct?: number;
  threshold?: number;
  rebalanceFreq?: string;
}

interface BacktestResults {
  kpis: any;
  chart_data: any[];
  trade_log?: any[];
}

const formatDate = (date: Date) => date.toISOString().split('T')[0];

const BacktestPairsView: React.FC = () => {
  const { currentUser } = useAuth();
  
  const [activeStrategy, setActiveStrategy] = useState<BacktestStrategy>(BacktestStrategy.PAIRS_TRADING);
  const [results, setResults] = useState<BacktestResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Check for pending pairs from insight view
  useEffect(() => {
    const pending = localStorage.getItem('aiq_navigate_pairs');
    if (pending) {
      localStorage.removeItem('aiq_navigate_pairs');
      try {
        const { asset1, asset2 } = JSON.parse(pending);
        if (asset1 && asset2) {
          setPairsParams(prev => ({ ...prev, asset1, asset2 }));
        }
      } catch {}
    }
  }, []);

  // Pairs Trading params
  const [pairsParams, setPairsParams] = useState<BacktestParams>({
    asset1: '',
    asset2: '',
    days: 730,
    window: 30,
    entryZ: 2.0,
    exitZ: 0.5,
  });
  
  // Alpha Portfolio params
  const [alphaParams, setAlphaParams] = useState<BacktestParams>({
    universe: 'SP100',
    startDate: formatDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
    endDate: formatDate(new Date()),
    portfolioSize: 10,
    lookbackDays: 30,
    alphaMetric: 'alpha_score',
    buyDipPct: 0.0,
    sellDecayPct: 10.0,
    threshold: 0.5,
    rebalanceFreq: 'D',
  });

  const handleRunPairsBacktest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!pairsParams.asset1 || !pairsParams.asset2) {
      toast.error("Please enter both ticker symbols");
      return;
    }

    if (!currentUser) {
      toast.error("Please log in to run backtests");
      return;
    }

    setIsLoading(true);
    setResults(null);
    const toastId = toast.loading("Running pairs backtest...");

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/backtest/pairs', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pairsParams),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Backtest failed');
      }

      const data = await response.json();
      setResults(data);
      toast.success("Backtest complete!", { id: toastId });
    } catch (error: any) {
      toast.error(`Backtest failed: ${error.message}`, { id: toastId });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunAlphaBacktest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      toast.error("Please log in to run backtests");
      return;
    }

    setIsLoading(true);
    setResults(null);
    const toastId = toast.loading("Running alpha portfolio backtest...");

    try {
      const token = await currentUser.getIdToken();
      
      // Convert threshold from percentage to decimal
      const paramsForApi = {
        ...alphaParams,
        threshold: (alphaParams.threshold || 0) / 100.0
      };
      
      const response = await fetch('/api/backtest/alpha_portfolio', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paramsForApi),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Backtest failed');
      }

      const data = await response.json();
      setResults(data);
      toast.success("Backtest complete!", { id: toastId });
    } catch (error: any) {
      toast.error(`Backtest failed: ${error.message}`, { id: toastId });
    } finally {
      setIsLoading(false);
    }
  };

  const renderPairsForm = () => (
    <form onSubmit={handleRunPairsBacktest}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Asset 1 (Ticker)</label>
          <input
            type="text"
            value={pairsParams.asset1 || ''}
            onChange={(e) => setPairsParams({...pairsParams, asset1: e.target.value.trim().toUpperCase()})}
            placeholder="e.g., AAPL"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Asset 2 (Ticker)</label>
          <input
            type="text"
            value={pairsParams.asset2 || ''}
            onChange={(e) => setPairsParams({...pairsParams, asset2: e.target.value.trim().toUpperCase()})}
            placeholder="e.g., MSFT"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Lookback Days</label>
          <input
            type="number"
            value={pairsParams.days || 730}
            onChange={(e) => setPairsParams({...pairsParams, days: parseInt(e.target.value) || 730})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="30"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Rolling Window</label>
          <input
            type="number"
            value={pairsParams.window || 30}
            onChange={(e) => setPairsParams({...pairsParams, window: parseInt(e.target.value) || 30})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="5"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Entry Z-Score</label>
          <input
            type="number"
            value={pairsParams.entryZ || 2.0}
            onChange={(e) => setPairsParams({...pairsParams, entryZ: parseFloat(e.target.value) || 2.0})}
            step="0.1"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Exit Z-Score</label>
          <input
            type="number"
            value={pairsParams.exitZ || 0.5}
            onChange={(e) => setPairsParams({...pairsParams, exitZ: parseFloat(e.target.value) || 0.5})}
            step="0.1"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
      >
        <Play size={18} />
        {isLoading ? 'Running...' : 'Run Pairs Backtest'}
      </button>
    </form>
  );

  const renderAlphaForm = () => (
    <form onSubmit={handleRunAlphaBacktest}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
          <input
            type="date"
            value={alphaParams.startDate || ''}
            onChange={(e) => setAlphaParams({...alphaParams, startDate: e.target.value})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
          <input
            type="date"
            value={alphaParams.endDate || ''}
            onChange={(e) => setAlphaParams({...alphaParams, endDate: e.target.value})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Stock Universe</label>
          <select
            value={alphaParams.universe || 'SP100'}
            onChange={(e) => setAlphaParams({...alphaParams, universe: e.target.value})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="SP100">S&P 100</option>
            <option value="NASDAQ100">NASDAQ 100</option>
            <option value="WATCHLIST">My Watchlist</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Alpha Lookback (days)
            <span title="Days to calculate alpha scores" className="inline-block ml-1">
              <Info size={14} className="text-slate-400" />
            </span>
          </label>
          <input
            type="number"
            value={alphaParams.lookbackDays || 30}
            onChange={(e) => setAlphaParams({...alphaParams, lookbackDays: parseInt(e.target.value) || 30})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="10"
            max="365"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Rank By</label>
          <select
            value={alphaParams.alphaMetric || 'alpha_score'}
            onChange={(e) => setAlphaParams({...alphaParams, alphaMetric: e.target.value})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="alpha_score">Total Alpha (Bull + Bear)</option>
            <option value="bull_alpha">Bull Alpha Only</option>
            <option value="bear_alpha">Bear Alpha Only</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Max Portfolio Size</label>
          <input
            type="number"
            value={alphaParams.portfolioSize || 10}
            onChange={(e) => setAlphaParams({...alphaParams, portfolioSize: parseInt(e.target.value) || 10})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="1"
            max="50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Buy on Dip (%)
            <span title="Buy when stock dips more than this %" className="inline-block ml-1">
              <Info size={14} className="text-slate-400" />
            </span>
          </label>
          <input
            type="number"
            value={alphaParams.buyDipPct || 0}
            onChange={(e) => setAlphaParams({...alphaParams, buyDipPct: parseFloat(e.target.value) || 0})}
            step="0.5"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Sell on Alpha Decay (%)
            <span title="Sell when alpha drops below this threshold" className="inline-block ml-1">
              <Info size={14} className="text-slate-400" />
            </span>
          </label>
          <input
            type="number"
            value={alphaParams.sellDecayPct || 10}
            onChange={(e) => setAlphaParams({...alphaParams, sellDecayPct: parseFloat(e.target.value) || 10})}
            step="5"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Significance Threshold (%)
            <span title="Minimum daily move to be considered significant" className="inline-block ml-1">
              <Info size={14} className="text-slate-400" />
            </span>
          </label>
          <input
            type="number"
            value={alphaParams.threshold || 0.5}
            onChange={(e) => setAlphaParams({...alphaParams, threshold: parseFloat(e.target.value) || 0.5})}
            step="0.05"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
      >
        <Activity size={18} />
        {isLoading ? 'Running...' : 'Run Alpha Backtest'}
      </button>
    </form>
  );

  const renderPairsResults = () => {
    if (!results) return null;

    return (
      <>
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Total Return</h4>
            <p className={`text-2xl font-bold ${results.kpis.total_return_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {results.kpis.total_return_pct.toFixed(2)}%
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Sharpe Ratio</h4>
            <p className="text-2xl font-bold text-slate-900">
              {results.kpis.sharpe_ratio.toFixed(2)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Max Drawdown</h4>
            <p className="text-2xl font-bold text-red-600">
              {results.kpis.max_drawdown_pct.toFixed(2)}%
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Trades</h4>
            <p className="text-2xl font-bold text-slate-900">
              {results.kpis.num_trades}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Hedge Ratio</h4>
            <p className="text-2xl font-bold text-slate-900">
              {results.kpis.hedge_ratio.toFixed(4)}
            </p>
          </div>
        </div>

        {/* Trade Statistics (if available) */}
        {results.kpis.win_rate_pct !== undefined && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h4 className="text-sm font-medium text-slate-600 mb-1">Win Rate</h4>
              <p className={`text-2xl font-bold ${results.kpis.win_rate_pct >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
                {results.kpis.win_rate_pct.toFixed(1)}%
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h4 className="text-sm font-medium text-slate-600 mb-1">Avg Win</h4>
              <p className="text-2xl font-bold text-green-600">
                +{results.kpis.avg_win_pct.toFixed(2)}%
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h4 className="text-sm font-medium text-slate-600 mb-1">Avg Loss</h4>
              <p className="text-2xl font-bold text-red-600">
                {results.kpis.avg_loss_pct.toFixed(2)}%
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h4 className="text-sm font-medium text-slate-600 mb-1">Profit Factor</h4>
              <p className="text-2xl font-bold text-slate-900">
                {results.kpis.profit_factor >= 999 ? '∞' : results.kpis.profit_factor.toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h4 className="text-sm font-medium text-slate-600 mb-1">Sortino</h4>
              <p className="text-2xl font-bold text-slate-900">
                {results.kpis.sortino_ratio.toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Charts */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Strategy Equity Curve</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={results.chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="Date" tick={{ fontSize: 12 }} stroke="#64748b" />
              <YAxis tickFormatter={(tick) => tick.toFixed(2)} tick={{ fontSize: 12 }} stroke="#64748b" />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
              <Legend />
              <Line type="monotone" dataKey="equity_curve" name="Strategy Equity" stroke="#2563eb" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Z-Score & Spread Analysis</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={results.chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="Date" tick={{ fontSize: 12 }} stroke="#64748b" />
              <YAxis yAxisId="left" orientation="left" stroke="#2563eb" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" stroke="#10b981" tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
              <Legend />
              <ReferenceLine yAxisId="left" y={pairsParams.entryZ} label="Short Entry" stroke="#ef4444" strokeDasharray="3 3" />
              <ReferenceLine yAxisId="left" y={-(pairsParams.entryZ || 2)} label="Long Entry" stroke="#10b981" strokeDasharray="3 3" />
              <ReferenceLine yAxisId="left" y={0} stroke="#94a3b8" />
              <Line yAxisId="left" type="monotone" dataKey="z_score" name="Z-Score" stroke="#2563eb" dot={false} strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="spread" name="Spread" stroke="#10b981" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </>
    );
  };

  const renderAlphaResults = () => {
    if (!results) return null;

    return (
      <>
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Total Return</h4>
            <p className={`text-2xl font-bold ${results.kpis.total_return_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {results.kpis.total_return_pct.toFixed(2)}%
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Sharpe Ratio</h4>
            <p className="text-2xl font-bold text-slate-900">
              {results.kpis.sharpe_ratio.toFixed(2)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Max Drawdown</h4>
            <p className="text-2xl font-bold text-red-600">
              {results.kpis.max_drawdown_pct.toFixed(2)}%
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-600 mb-1">Total Trades</h4>
            <p className="text-2xl font-bold text-slate-900">
              {results.kpis.num_trades}
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Portfolio Performance vs Benchmark</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={results.chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#64748b" />
              <YAxis tickFormatter={(v) => v.toFixed(0)} tick={{ fontSize: 12 }} stroke="#64748b" />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
              <Legend />
              <Line type="monotone" dataKey="portfolioValue" name="Alpha Portfolio" stroke="#2563eb" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="benchmarkValue" name="SPY Benchmark" stroke="#10b981" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Trade Log */}
        {results.trade_log && results.trade_log.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Trade Log</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Ticker</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">P&L</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Reason</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {results.trade_log.slice(0, 50).map((trade: any, index: number) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-900">{trade.date}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${trade.action === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {trade.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{trade.ticker}</td>
                      <td className="px-4 py-3 text-sm text-slate-900">${trade.price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm">
                        {trade.pnl_pct !== null ? (
                          <span className={trade.pnl_pct >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                            {trade.pnl_pct.toFixed(2)}%
                          </span>
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{trade.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="h-full overflow-y-auto p-6 bg-slate-50">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <BarChart3 className="text-blue-600" />
          Strategy Backtesting
        </h1>
        <p className="text-slate-600 mt-1">
          Test trading strategies with historical data
        </p>
      </div>

      {/* Strategy Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => { setActiveStrategy(BacktestStrategy.PAIRS_TRADING); setResults(null); }}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeStrategy === BacktestStrategy.PAIRS_TRADING
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <TrendingUp size={18} />
            Pairs Trading
          </button>
          
          <button
            onClick={() => { setActiveStrategy(BacktestStrategy.ALPHA_PORTFOLIO); setResults(null); }}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeStrategy === BacktestStrategy.ALPHA_PORTFOLIO
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Activity size={18} />
            Alpha Portfolio
          </button>

          <button
            onClick={() => { setActiveStrategy(BacktestStrategy.BASKET_SPREAD); setResults(null); }}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeStrategy === BacktestStrategy.BASKET_SPREAD
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <TrendingDown size={18} />
            Basket Spread
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6">
          {activeStrategy === BacktestStrategy.PAIRS_TRADING && renderPairsForm()}
          {activeStrategy === BacktestStrategy.ALPHA_PORTFOLIO && renderAlphaForm()}
          {activeStrategy === BacktestStrategy.BASKET_SPREAD && (
            <div className="text-center py-12 text-slate-500">
              <TrendingDown size={40} className="mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Basket Spread Strategy</h3>
              <p className="text-sm">Long/short basket backtesting — coming in the next update.</p>
              <p className="text-xs text-slate-400 mt-2">Define long and short baskets, backtest the spread with equal or custom weights.</p>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-6">
          {activeStrategy === BacktestStrategy.PAIRS_TRADING && renderPairsResults()}
          {activeStrategy === BacktestStrategy.ALPHA_PORTFOLIO && renderAlphaResults()}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !results && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
          <BarChart3 size={64} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-800 mb-2">Ready to Backtest</h3>
          <p className="text-slate-600 mb-4">
            Configure your strategy parameters above and run a backtest to see historical performance.
          </p>
          <div className="text-sm text-slate-500 space-y-1 text-left max-w-2xl mx-auto">
            <p><strong>Pairs Trading:</strong> Mean-reversion strategy for correlated assets</p>
            <p><strong>Alpha Portfolio:</strong> Buy top-ranked stocks by bull/bear alpha scores</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BacktestPairsView;
